import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportTicketEvent } from '../../../models/Support/ISupportTicketEvent';
import { SupportEventType } from '../../../models/Support/SupportEnums';
import { styles } from './styles';
import { formatDateTime } from './supportTicketPresentation';

export interface EventItemProps {
	event: ISupportTicketEvent;
	agentNameMap?: Record<string, string>;
}

/**
 * Composes a human-readable summary for a ticket event when the API does not
 * supply a pre-rendered `description`. Uses the structured `data` payload where
 * available and falls back to a sensible default for each {@link SupportEventType}.
 */
const composeEventDescription = (event: ISupportTicketEvent, agentNameMap: Record<string, string>): string => {
	const data = event.data ?? {};
	const actor =
		resolveAgentLabel(event.actorName, agentNameMap) ||
		resolveAgentLabel(event.actorUserId, agentNameMap) ||
		resolveAgentLabel(data.actorName, agentNameMap);

	switch (event.eventType) {
		case SupportEventType.Assigned: {
			const assignee =
				resolveAgentLabel(data.assigneeId, agentNameMap) ||
				resolveAgentLabel(data.assignee, agentNameMap) ||
				resolveAgentLabel(data.to, agentNameMap) ||
				actor ||
				'an agent';
			return `Assigned to ${assignee}`;
		}
		case SupportEventType.Unassigned:
			return 'Unassigned';
		case SupportEventType.StatusChanged:
			return data.from && data.to
				? `Status changed from ${data.from} to ${data.to}`
				: data.to
					? `Status changed to ${data.to}`
					: 'Status changed';
		case SupportEventType.PriorityChanged:
			return data.from && data.to
				? `Priority changed from ${data.from} to ${data.to}`
				: data.to
					? `Priority changed to ${data.to}`
					: 'Priority changed';
		case SupportEventType.CategoryChanged:
			return data.from && data.to
				? `Category changed from ${data.from} to ${data.to}`
				: data.to
					? `Category changed to ${data.to}`
					: 'Category changed';
		case SupportEventType.CustomerChanged:
			return data.from && data.to
				? `Customer changed from ${data.from} to ${data.to}`
				: data.to
					? `Customer changed to ${data.to}`
					: 'Customer changed';
		case SupportEventType.TicketLinked:
			return data.ticket || data.to ? `Linked ticket ${data.ticket || data.to}` : 'Linked a ticket';
		case SupportEventType.TicketUnlinked:
			return data.ticket || data.to ? `Unlinked ticket ${data.ticket || data.to}` : 'Unlinked a ticket';
		case SupportEventType.Merged:
			return data.from ? `Merged ticket ${data.from} into this ticket` : 'Merged a ticket into this ticket';
		case SupportEventType.GitHubIssueLinked:
			return data.issue || data.to ? `Linked GitHub issue ${data.issue || data.to}` : 'Linked a GitHub issue';
		case SupportEventType.GitHubIssueUnlinked:
			return data.issue || data.to ? `Unlinked GitHub issue ${data.issue || data.to}` : 'Unlinked a GitHub issue';
		case SupportEventType.GitHubIssueCreated:
			return data.issue || data.to ? `Created GitHub issue ${data.issue || data.to}` : 'Created a GitHub issue';
		case SupportEventType.GitHubIssueUpdated:
			return data.issue || data.to ? `Updated GitHub issue ${data.issue || data.to}` : 'Updated a GitHub issue';
		case SupportEventType.GitHubIssueSynced:
			return data.issue || data.to ? `Synced GitHub issue ${data.issue || data.to}` : 'Synced a GitHub issue';
		case SupportEventType.Forwarded:
			return data.to ? `Forwarded to ${data.to}` : 'Forwarded the conversation';
		case SupportEventType.Rated:
			return data.rating || data.to ? `Rated ${data.rating || data.to}` : 'Rated this ticket';
		default:
			return 'Activity';
	}
};

const resolveAgentLabel = (value: string | undefined, agentNameMap: Record<string, string>) => {
	if (!value) return undefined;
	return agentNameMap[value] ?? value;
};

/**
 * A muted, centered activity row in the ticket timeline. Renders a structured
 * ticket event (the replacement for the old `ThreadType.System` pseudo-thread
 * pills), reusing the `threadSystem` look. Shows the event description, a
 * timestamp, and the actor name when present.
 */
export const EventItem: React.FC<EventItemProps> = ({ event, agentNameMap = {} }) => {
	const actorName =
		resolveAgentLabel(event.actorName, agentNameMap) ||
		resolveAgentLabel(event.actorUserId, agentNameMap) ||
		resolveAgentLabel(event.data?.actorName, agentNameMap);
	const description = event.description || composeEventDescription(event, agentNameMap);
	const timestamp = formatDateTime(event.creationDate);

	return (
		<div className={css(styles.thread, styles.threadSystem)} role='note'>
			<span>{description}</span>
			{actorName && (
				<>
					{' by '}
					<span className={css(styles.eventActor)}>{actorName}</span>
				</>
			)}
			<span className={css(styles.eventTime)}> &middot; {timestamp}</span>
		</div>
	);
};
